import { useDecideReview, useReviewQueue } from '../api/hooks.js';
import { Button } from '../components/Button.js';
import { Card, CardFoot } from '../components/Card.js';
import { cx, MONO } from '../components/classes.js';
import { EmptyState, ProblemNote, SkeletonRows } from '../components/Notes.js';
import { Main, PageHeader } from '../components/PageHeader.js';
import { formatDate, sourceLabel, titleCase, yearOf } from '../lib/format.js';
import type { ReviewCandidate, ReviewItem } from '../lib/types.js';
import { reviewKey, useReviewStore } from '../store/review.js';
import { canIssue, useSessionStore } from '../store/session.js';

export function candidateLine(c: ReviewCandidate): string {
  const s = c.summary;
  const bits: string[] = [];
  if (typeof s['tpin'] === 'string') bits.push(`TPIN ${s['tpin']}`);
  const reg = typeof s['registered'] === 'string' ? yearOf(s['registered']) : null;
  if (reg) bits.push(`reg. ${reg}`);
  const types = s['tax_types'];
  if (Array.isArray(types))
    bits.push(types.length ? types.map(String).slice(0, 3).join(', ') : 'no tax types');
  if (typeof s['status'] === 'string') bits.push(String(s['status']).toLowerCase());
  return bits.join(' · ');
}

/** Wireframe §10: humans resolve the 0.75–0.92 band; "Neither" is always offered. */
export function Review() {
  const queue = useReviewQueue();
  const decide = useDecideReview();
  const role = useSessionStore((s) => s.role);
  const choices = useReviewStore((s) => s.choices);
  const skipped = useReviewStore((s) => s.skipped);
  const choose = useReviewStore((s) => s.choose);
  const skip = useReviewStore((s) => s.skip);
  const clear = useReviewStore((s) => s.clear);

  const items = queue.data?.items ?? [];
  const waiting = items.filter((i) => !skipped[reviewKey(i.entity.id, i.source)]);
  const current: ReviewItem | undefined = waiting[0] ?? items[0];
  const key = current ? reviewKey(current.entity.id, current.source) : '';
  const choice = key ? choices[key] : undefined;
  const mayDecide = canIssue(role);

  const confirm = () => {
    if (!current || choice === undefined) return;
    decide.mutate(
      { entityId: current.entity.id, source: current.source, sourceRecordId: choice },
      { onSuccess: () => clear(key) },
    );
  };

  return (
    <Main>
      <PageHeader
        title="Needs review"
        line={
          queue.data
            ? `${items.length} ${items.length === 1 ? 'match' : 'matches'} waiting · your choice is recorded with your name`
            : ' '
        }
      />
      {queue.isError ? <ProblemNote error={queue.error} /> : null}
      {queue.isPending ? (
        <SkeletonRows rows={3} className="rounded-card border border-line bg-surface" />
      ) : !current ? (
        <Card>
          <EmptyState title="Nothing to review">
            When two records score close (0.75–0.92) for one entity, the pair waits here
            for a person. Nothing is linked automatically in that band.
          </EmptyState>
        </Card>
      ) : (
        <Card lift label="Review item">
          <div className="flex flex-col gap-2 px-8 pt-7 pb-5">
            <h2 className="m-0 text-[22px] font-semibold tracking-[-0.01em]">
              Which {sourceLabel(current.source)} record belongs to{' '}
              {titleCase(current.entity.name)}?
            </h2>
            <p className="m-0 max-w-[70ch] text-[14px] text-mute">
              {current.candidates.length === 1
                ? 'One candidate'
                : `${current.candidates.length} candidates`}{' '}
              scored close. Choose one, or leave unlinked. Your choice is recorded with
              your name and can be undone by an admin.
            </p>
          </div>
          <div role="radiogroup" aria-label="Candidates" className="flex flex-col">
            {current.candidates.map((c) => {
              const selected = choice === c.source_record_id;
              return (
                <label
                  key={c.source_record_id}
                  className={cx(
                    'grid cursor-pointer grid-cols-[18px_minmax(0,1fr)_60px] items-center gap-4 border-t border-line px-8 py-4',
                    selected && 'bg-sunk',
                  )}
                >
                  <input
                    type="radio"
                    name={`review-${key}`}
                    checked={selected}
                    onChange={() => choose(key, c.source_record_id)}
                    className="size-[18px] accent-accent"
                  />
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[15px] font-semibold">{titleCase(c.name)}</span>
                    <span className={cx(MONO, 'text-[12px] text-mute')}>
                      {candidateLine(c) || c.source_key} · read {formatDate(c.fetched_at)}
                    </span>
                  </span>
                  <span className={cx(MONO, 'text-right text-[13px]')}>
                    {c.confidence.toFixed(2)}
                  </span>
                </label>
              );
            })}
            <label
              className={cx(
                'grid cursor-pointer grid-cols-[18px_minmax(0,1fr)] items-center gap-4 border-t border-line px-8 py-4',
                choice === null && 'bg-sunk',
              )}
            >
              <input
                type="radio"
                name={`review-${key}`}
                checked={choice === null}
                onChange={() => choose(key, null)}
                className="size-[18px] accent-accent"
              />
              <span className="text-[15px]">Neither — leave unlinked</span>
            </label>
          </div>
          {decide.isError ? (
            <div className="px-8 pb-4">
              <ProblemNote error={decide.error} />
            </div>
          ) : null}
          <div className="flex gap-3 border-t border-line px-8 py-5">
            <Button
              variant="primary"
              onClick={confirm}
              disabled={!mayDecide || choice === undefined || decide.isPending}
            >
              {decide.isPending
                ? 'Recording…'
                : choice === null
                  ? 'Leave unlinked'
                  : 'Confirm link'}
            </Button>
            <Button onClick={() => skip(key)} disabled={decide.isPending}>
              Skip for now
            </Button>
            {!mayDecide ? (
              <span className="self-center text-[13px] text-mute">
                Your role is read-only; a verifier or admin decides.
              </span>
            ) : null}
          </div>
          <CardFoot>
            {waiting.length > 1
              ? `${waiting.length - 1} more waiting after this one.`
              : 'Last one waiting.'}
          </CardFoot>
        </Card>
      )}
    </Main>
  );
}
